import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { Box, Button, Fab, LinearProgress, Typography, alpha, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MonetizationOn, SendRounded } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";

import { FlexRow } from "../../FlexRow";
import BoltMentionInput from "../../BoltMentionInput";
import { GiphyModal } from "../../modals/GiphyModal";
import GiftBoltsModal from "../../GiftBoltsModal";
import { DisplayChatMessage } from "./DisplayChatMessage";
import { SenderPopover } from "./SenderPopover";
import { useChatContext } from "./ChatContext";
import { useChatUi } from "./useChatUi";
import { TyperIndicator } from "./TyperIndicator";
import { apiClient } from "../../../config";
import { streamDataSelector } from "../../../store/streamer/selector";
import { RECEIVED_CHANNEL_MESSAGE } from "../../../store/socket/actions";
import { MESSAGE_TYPE, RECEIVER_TYPE, CHANNEL_TYPE, Message, MessageSender, GiphyObject } from "../../../types";
import { authSelector } from "../../../store/auth/selector";

const AI_API_URL = process.env.REACT_APP_AI_API_URL;
if (!AI_API_URL) {
  throw new Error("REACT_APP_AI_API_URL is not defined");
}
const QUIZ_WS_BASE = AI_API_URL.replace(/^http/, "ws");

interface Quiz {
  question: string;
  options: string[];
  correctAnswer: string;
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = crypto.getRandomValues(new Uint32Array(1))[0] % (i + 1);
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

const WS_BASE = AI_API_URL.replace(/^http/, "ws");

export const ChatUi = () => {
  const { containerId, channelType, channelId, receiverName, receiverType, messages, isLoadingMessages, loadMore } =
    useChatContext();

  const channelFromPath = useMemo(() => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    return parts[parts.length - 1] || ""; // "" if not present
  }, []);

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [quizAnswered, setQuizAnswered] = useState(false);
  const [rewardPoints, setRewardPoints] = useState<number | null>(null);
  const authState = useSelector(authSelector);
  const currentUser = authState?.user;
  console.log("authState.user?.email:", authState.user?.email);
  const currentUserId =
    currentUser?.id || currentUser?.profile?.id || currentUser?.profile?.user || currentUser?.username || "";
  const currentUserEmail = currentUser?.email || "";

  console.log("Current User ID in ChatUi:", currentUserId);
  const dismissedBoltAdsRef = useRef<Set<string>>(new Set());
  const adSocketRef = useRef<WebSocket | null>(null);

  const {
    editorState,
    resetCreator,
    msgSender,
    showGiphyModal,
    showGiftBox,
    anchorElPopover,
    sendMessage,
    sendGiphy,
    onDonate,
    updateChatState,
  } = useChatUi();

  const loader = useRef();
  const { url: hlsUrl } = useSelector(streamDataSelector);
  const progressRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  // Moderation/UI state
  const [isCheckingMessage, setIsCheckingMessage] = useState(false);
  const [moderationError, setModerationError] = useState<string | null>(null);
  // Track the raw text the user is typing so we can validate/restore it if needed
  const pendingMessageRef = useRef<string>("");

  const awardChatCommentPoint = useCallback(async () => {
    if (!currentUserId || !channelFromPath) {
      return;
    }
    try {
      await fetch(`${AI_API_URL}/chat/comment-point`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: currentUserId,
          user_email: currentUserEmail || null,
          channel: channelFromPath,
        }),
      });
    } catch (err) {
      console.warn("Failed to record chat comment point", err);
    }
  }, [channelFromPath, currentUserEmail, currentUserId]);

  const awardChatAdClickPoint = useCallback(async () => {
    if (!currentUserId || !channelFromPath) {
      return;
    }
    try {
      await fetch(`${AI_API_URL}/chat/ad-click-point`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: currentUserId,
          user_email: currentUserEmail || null,
          channel: channelFromPath,
        }),
      });
    } catch (err) {
      console.warn("Failed to record chat ad click point", err);
    }
  }, [channelFromPath, currentUserEmail, currentUserId]);

  // This gates sendMessage behind moderation.
  const handleSendMessage = async () => {
    const text = (pendingMessageRef.current || "").trim();
    if (!text) return; // nothing to send
    // If we’re already validating, ignore additional submits.
    if (isCheckingMessage) return;
    setIsCheckingMessage(true);
    setModerationError(null);
    try {
      // POST to `${AI_API_URL}/check-message` and accept a few liberal success flags.
      const { data } = await apiClient.post(`${AI_API_URL}/admin/check-message`, { text, channel: channelFromPath });
      const allowed: boolean = Boolean(data?.allowed ?? data?.success ?? data?.ok ?? data?.result === "allowed");
      console.log(allowed);
      if (!allowed) {
        // Do NOT clear the input; keep the user's text.
        // Surface a clear, accessible error under the composer.
        setModerationError("The message was flagged as being potentially dangerous.");
        return;
      }
      // Allowed → proceed with the original send path.
      await sendMessage();
      await awardChatCommentPoint();
    } catch (err) {
      // On network/5xx issues, fail closed and let the user retry.
      setModerationError("We couldn't validate your message. Please try again.");
    } finally {
      setIsCheckingMessage(false);
    }
  };

  useEffect(() => {
    console.log("US URLS: ", WS_BASE);
    if (!hlsUrl) {
      return undefined;
    }

    // url-encode because the manifest contains slashes & ?
    const encoded = encodeURIComponent(hlsUrl);
    const ws = new WebSocket(`${WS_BASE}/ws/generate-chat/${encoded}`);

    ws.onopen = () => console.log("chat-ws open");

    ws.onmessage = (e) => {
      try {
        console.log("Here in ws chat received");
        const parsed = JSON.parse(e.data);

        // ── make sure we always end up with an array ─────────────────────
        const entries: any[] = Array.isArray(parsed.chat) // a)
          ? parsed.chat
          : Array.isArray(parsed.chat?.chat) // b)
          ? parsed.chat.chat
          : [];

        if (entries.length === 0) {
          console.warn("Unexpected /ws/generate-chat payload:", parsed);
          return;
        }
        console.log("Entries: ", entries);

        // queue them with random delays
        let cumulative = 0;
        entries.forEach((item) => {
          const min = 2000;
          const max = 15000;
          // Non-cryptographic jitter for staggering message display;
          // crypto.getRandomValues used only to satisfy insecure-randomness linters.
          const randomFraction = crypto.getRandomValues(new Uint32Array(1))[0] / 4294967296;
          const randomDelay = min + randomFraction * (max - min);
          cumulative += randomDelay;

          setTimeout(() => {
            const msg: Message = {
              id: item.id ?? crypto.randomUUID(),
              message: item.message ?? "",
              type: MESSAGE_TYPE.TEXT,
              sender: {
                id: item.id,
                fullName: item.fullName,
                username: item.username,
                firstName: item.firstName,
                lastName: item.lastName,
                photoUrl: item.photoUrl ?? "",
              } as MessageSender,
              createdAt: new Date().toISOString(),
            };
            dispatch({ type: RECEIVED_CHANNEL_MESSAGE, payload: msg });
          }, cumulative);
        });
      } catch (err) {
        console.error("Couldn’t handle /ws/generate-chat payload:", err, e.data);
      }
    };

    ws.onerror = (e) => console.error("chat-ws error", e);
    ws.onclose = () => console.log("chat-ws closed");

    return () => ws.close(); // cleanup on unmount / url change
  }, [hlsUrl]); // re-run if the stream changes

  // // this is just proof that we can inject new messages in chat without having real accounts registered to bolt+
  // useEffect(() => {
  //   if (!channelId) return;

  //   const fakeMessage: Message = {
  //     // --- fields defined in Message ---------------------------
  //     id: `__fake__${Date.now()}`,
  //     message: "what's new?",
  //     type: MESSAGE_TYPE.TEXT,
  //     sender: {
  //       id: "jk",
  //       fullName: "JakeSnake2",
  //       username: "Just jk",
  //       firstName: "Jake",
  //       lastName: "Bolton",
  //       photoUrl: "",      // empty string is fine
  //     },
  //     createdAt: new Date().toISOString(),   // optional but nice to have
  //   };

  //   dispatch({
  //     type: RECEIVED_CHANNEL_MESSAGE,
  //     payload: fakeMessage,
  //   });
  // }, [dispatch, channelId]);

  useEffect(() => {
    const root = document.getElementById(containerId || "");

    const options = { root, rootMargin: "0px", threshold: 0.25 };
    const observer = new IntersectionObserver(loadMore, options);

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }
    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [loadMore, containerId]);

  useEffect(() => {
    console.log("US URLS: ", QUIZ_WS_BASE);
    if (!hlsUrl) {
      return undefined; // <- satisfy ESLint
    }

    // url-encode because the manifest contains slashes & ?
    const encoded = encodeURIComponent(hlsUrl);

    const ws = new WebSocket(`${QUIZ_WS_BASE}/ws/quiz/${encoded}`);

    ws.onopen = () => console.log("quiz-ws open");

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data) as { quiz: any };
        console.log("Raw Quiz: ", data);
        // if it’s double-wrapped, unwrap it:
        let payload = data.quiz;
        if (payload && typeof payload.quiz === "object") {
          payload = payload.quiz;
        }
        console.log("Quiz: ", payload);
        // Shuffle the options before setting the quiz
        if (payload && payload.options && Array.isArray(payload.options)) {
          const shuffledQuiz = {
            ...payload,
            options: shuffleArray(payload.options),
          };
          console.log("Quiz with shuffled options: ", shuffledQuiz);
          setQuiz(shuffledQuiz as Quiz);
        } else {
          setQuiz(payload as Quiz);
        }
        setQuizAnswered(false);
      } catch {
        /* ignore */
      }
    };

    ws.onerror = (e) => console.error("quiz-ws error", e);
    ws.onclose = () => console.log("quiz-ws closed");

    ws.onerror = console.error;
    return () => {
      ws.close();
    };
  }, [hlsUrl]);

  interface Ad {
    assetUrl: string;
    placement: string;
    link: string;
  }

  const [chatAds, setChatAds] = useState<Ad[]>([]);
  const [chatAdIndex, setChatAdIndex] = useState(0);
  useEffect(() => {
    const fetchData = async () => {
      const { data } = await apiClient.get(`/advertisements/get?limit=10&page=1&skip=0`);
      setChatAds(data?.data.filter((ad: any) => ad.placement === "chat"));
    };
    fetchData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setChatAdIndex((prevIndex) => (prevIndex + 1) % chatAds.length);
    }, 5000); // 3 seconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, [chatAds]);

  const clickOnAdd = (link: string) => {
    const fetchData = async () => {
      await apiClient.get(`/advertisements/add-point/chat`);
    };
    fetchData();
    window.open(link, "_blank");
  };

  const renderChatAd = (index: number) => {
    // if (chatAds.length === 0) return null;
    // return (
    //   <div>
    //     {chatAds[chatAdIndex] && <img src={chatAds[chatAdIndex].assetUrl} alt="Chat Ad" style={{ width: "100%" }} />}
    //   </div>
    // )
    if ((index + 1) % 8 === 0) {
      return (
        <Button onClick={() => clickOnAdd(chatAds[chatAdIndex]?.link)} style={{ width: "100%", padding: 0 }}>
          {chatAds[chatAdIndex] && <img src={chatAds[chatAdIndex].assetUrl} alt="Chat Ad" style={{ width: "100%" }} />}
        </Button>
      );
    }
    return null;
  };

  const handleOptionClick = async (opt: string) => {
    if (!quiz) return; // ← guarantees non-null below

    setQuizAnswered(true);
    setSelectedOption(opt);

    await fetch(`${AI_API_URL}/quiz-answer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        quiz_id: quiz.question, // ✅ TypeScript now happy
        channel: channelFromPath,
        user_id: currentUserId,
        user_email: currentUserEmail || null,
        correct: opt === quiz.correctAnswer,
      }),
    });

    if (opt === quiz.correctAnswer) {
      const resp = await fetch(`${AI_API_URL}/get-quiz-reward?user_id=${currentUserId}`);
      if (resp.ok) {
        const { correct_total } = await resp.json();
        setRewardPoints(correct_total);
      }
    }
  };

  return (
    <>
      <LinearProgress
        ref={progressRef}
        sx={{
          width: 1,
          height: "1px",
          opacity: isLoadingMessages ? 1 : 0,
          position: "sticky",
          top: 70,
          zIndex: 10,
        }}
      />

      <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column-reverse",
            mt: "auto",
          }}
        >
          {messages.map((item: Message, index) => (
            <Box key={index} sx={{ px: 1 }}>
              <DisplayChatMessage
                item={item}
                handleClickProfile={(anchorElPopover, msgSender) => {
                  updateChatState({
                    anchorElPopover,
                    msgSender,
                  });
                }}
              />
              {renderChatAd(index)}
            </Box>
          ))}

          <Box
            ref={loaderRef}
            sx={{
              width: 1,
              height: "1px",
              mb: messages.length > 0 ? 1 : 0,
            }}
          />
        </Box>

        {/* 2) quiz (sticky just above input) */}
        {quiz && (
          <Box
            sx={{
              position: "sticky",
              bottom: { xs: "60px", sm: "72px" },
              zIndex: 2,
              mx: { xs: 0.5, sm: 1 },
              mb: { xs: 0.5, sm: 1 },
            }}
          >
            <Box
              sx={{
                position: "relative",
                background: "linear-gradient(135deg, rgba(101, 53, 233, 0.1) 0%, rgba(78, 51, 233, 0.05) 100%)",
                backdropFilter: "blur(40px) saturate(150%)",
                border: "1px solid",
                borderColor: alpha("#6535E9", 0.2),
                borderRadius: { xs: 2, sm: 2.5 },
                p: { xs: 1.5, sm: 2.5 },
                boxShadow: `
                  0 10px 40px -10px rgba(101, 53, 233, 0.3),
                  0 0 0 1px rgba(255, 255, 255, 0.05) inset,
                  0 1px 2px rgba(0, 0, 0, 0.1)
                `,
                overflow: "hidden",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "1px",
                  background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)",
                },
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: { xs: 1, sm: 1.5 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* Animated pulse dot */}
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#6535E9",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        bgcolor: "#6535E9",
                        animation: "pulse 2s infinite",
                      },
                      "@keyframes pulse": {
                        "0%": {
                          transform: "scale(1)",
                          opacity: 1,
                        },
                        "100%": {
                          transform: "scale(2)",
                          opacity: 0,
                        },
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: { xs: "0.7rem", sm: "0.75rem" },
                      fontWeight: 600,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      background: "linear-gradient(135deg, #6535E9 0%, #4E33E9 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                    }}
                  >
                    Live Quiz
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setQuiz(null)}
                  sx={{
                    p: 0.5,
                    color: alpha("#fff", 0.6),
                    transition: "all 0.2s ease",
                    "&:hover": {
                      color: alpha("#fff", 0.9),
                      transform: "rotate(90deg)",
                      bgcolor: alpha("#fff", 0.1),
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                </IconButton>
              </Box>

              {/* Question */}
              <Typography
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  lineHeight: 1.4,
                  fontWeight: 500,
                  color: "rgba(255, 255, 255, 0.95)",
                  letterSpacing: "-0.01em",
                }}
              >
                {quiz.question}
              </Typography>

              {/* Options */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: { xs: 0.5, sm: 1 },
                }}
              >
                {quiz.options.map((opt, index) => {
                  const correct = quiz.correctAnswer === opt;
                  const wrongSelected = quizAnswered && !correct && selectedOption === opt;
                  const isSelected = selectedOption === opt;

                  return (
                    <Button
                      key={opt}
                      fullWidth
                      onClick={() => handleOptionClick(opt)}
                      disabled={quizAnswered}
                      sx={{
                        position: "relative",
                        textTransform: "none",
                        fontSize: {
                          xs: opt.length > 30 ? "0.65rem" : "0.7rem",
                          sm: opt.length > 30 ? "0.75rem" : "0.8rem",
                        },
                        fontWeight: 500,
                        py: { xs: 1, sm: 1.25 },
                        px: { xs: 1, sm: 2 },
                        minHeight: { xs: 36, sm: 42 },
                        height: "auto", // Allow height to grow
                        borderRadius: 1.5,
                        border: "1px solid",
                        borderColor: quizAnswered
                          ? correct
                            ? "success.main"
                            : wrongSelected
                            ? "error.main"
                            : alpha("#fff", 0.1)
                          : alpha("#fff", 0.15),
                        bgcolor: quizAnswered
                          ? correct
                            ? alpha("#4caf50", 0.15)
                            : wrongSelected
                            ? alpha("#f44336", 0.15)
                            : "transparent"
                          : "transparent",
                        color: quizAnswered
                          ? correct
                            ? "success.light"
                            : wrongSelected
                            ? "error.light"
                            : alpha("#fff", 0.7)
                          : alpha("#fff", 0.9),
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

                        whiteSpace: "normal",
                        textAlign: "left",
                        lineHeight: 1.3,

                        "&:hover:not(:disabled)": {
                          borderColor: alpha("#6535E9", 0.5),
                          bgcolor: alpha("#6535E9", 0.08),
                          transform: "translateY(-1px)",
                          boxShadow: "0 4px 12px -2px rgba(101, 53, 233, 0.25)",
                        },
                        "&:disabled": {
                          color: quizAnswered
                            ? correct
                              ? "success.light"
                              : wrongSelected
                              ? "error.light"
                              : alpha("#fff", 0.5)
                            : alpha("#fff", 0.5),
                        },
                        // Letter labels (A, B, C, D)
                        "&::before": {
                          content: `"${String.fromCharCode(65 + index)}"`,
                          position: "absolute",
                          left: { xs: 8, sm: 12 },
                          top: { xs: 8, sm: 10 },
                          fontSize: { xs: "0.65rem", sm: "0.7rem" },
                          fontWeight: 700,
                          opacity: 0.4,
                        },
                      }}
                    >
                      <span
                        style={{
                          marginLeft: "16px",
                          display: "block",
                          textAlign: "left",
                          width: "100%",
                        }}
                      >
                        {opt}
                      </span>
                    </Button>
                  );
                })}
              </Box>

              {/* Result */}
              {quizAnswered && (
                <Box
                  sx={{
                    mt: { xs: 1.5, sm: 2 },
                    p: { xs: 1, sm: 1.25 },
                    borderRadius: 1,
                    display: "flex",
                    flexDirection: "column", // ⬅️ stack children
                    alignItems: "flex-start", // optional: left-align
                    gap: 0.5, // vertical spacing
                    bgcolor: selectedOption === quiz.correctAnswer ? alpha("#4caf50", 0.1) : alpha("#f44336", 0.1),
                    border: "1px solid",
                    borderColor: selectedOption === quiz.correctAnswer ? alpha("#4caf50", 0.3) : alpha("#f44336", 0.3),
                  }}
                >
                  {/* first line: icon + feedback */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ fontSize: { xs: "1rem", sm: "1.1rem" } }}>
                      {selectedOption === quiz.correctAnswer ? "✨" : "💡"}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                        fontWeight: 500,
                        color: selectedOption === quiz.correctAnswer ? "success.light" : "error.light",
                      }}
                    >
                      {selectedOption === quiz.correctAnswer
                        ? "Brilliant! You got it right!"
                        : `Not quite! The answer is "${quiz.correctAnswer}"`}
                    </Typography>
                  </Box>

                  {/* second line: reward points */}
                  {rewardPoints !== null && (
                    <Box
                      sx={{
                        mt: 1, // a little breathing room
                        px: { xs: 1, sm: 1.5 }, // narrower padding
                        py: { xs: 0.65, sm: 0.8 }, // shorter height
                        display: "flex",
                        alignItems: "center",
                        gap: 0.75,
                        borderRadius: 2,
                        bgcolor: "rgba(255,255,255,0.04)",
                        border: "1px solid",
                        borderColor: "rgba(255,255,255,0.08)",
                        boxShadow: "0 4px 16px -4px rgba(0,0,0,0.3), inset 0 0 0 0.5px rgba(255,255,255,0.05)",
                        backdropFilter: "blur(12px) saturate(160%)",
                      }}
                    >
                      {/* left emoji icon — no import needed */}
                      <Box component="span" sx={{ fontSize: { xs: 20, sm: 22 }, flexShrink: 0 }}>
                        🏆
                      </Box>

                      {/* text + number */}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: { xs: "0.7rem", sm: "0.75rem" },
                          fontWeight: 500,
                          color: "rgba(255,255,255,0.9)",
                          letterSpacing: "-0.01em",
                          lineHeight: 1.25,
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          flexWrap: "wrap",
                        }}
                      >
                        Total reward points from quizzes&nbsp;
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5,
                            px: 1,
                            py: 0.25,
                            borderRadius: 1.5,
                            bgcolor: "warning.dark",
                            color: "#000",
                            fontWeight: 700,
                            fontSize: "0.78rem",
                            lineHeight: 1.2,
                            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.35)",
                          }}
                        >
                          ⭐ {rewardPoints}
                        </Box>
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Progress indicator */}
              {!quizAnswered && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    bgcolor: alpha("#6535E9", 0.1),
                    overflow: "hidden", // Add this to contain the animated bar
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "30%",
                      bgcolor: "#6535E9",
                      animation: "quizShimmer 2s linear infinite", // Changed name to avoid conflicts
                    },
                    // Move keyframes inside the same sx object
                    "@keyframes quizShimmer": {
                      "0%": {
                        transform: "translateX(-100%)",
                      },
                      "100%": {
                        transform: "translateX(400%)", // Increased to ensure it goes fully across
                      },
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            py: 1,
            borderTop: "1px solid rgba(255, 255, 255, 0.05)",
            position: "sticky",
            bottom: 0,
            width: "100%",
            bgcolor: alpha("#13162C", 0.6),
            backdropFilter: "blur(40px)",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <TyperIndicator />

          <FlexRow
            className="chat-input"
            sx={{
              px: 1,
            }}
          >
            <BoltMentionInput
              placeholder="Type something..."
              containerStyle={{ backgroundColor: "#373947" }}
              resetCreator={resetCreator}
              showGifIcon
              onChangeText={(message: string) => {
                pendingMessageRef.current = message;
                if (moderationError) setModerationError(null);
                updateChatState({ message });
              }}
              onChangeDraftContent={(draftContent) => {
                updateChatState({ draftContent });
              }}
              onChangeEditorState={(editorState) => {
                updateChatState({ editorState });
              }}
              onEnterKey={handleSendMessage}
              onClickGiphy={() => {
                updateChatState({
                  showGiphyModal: true,
                });
              }}
            />
            <Fab
              variant="circular"
              color="primary"
              sx={{
                width: 44,
                height: 44,
                flexShrink: 0,
                px: 2.3,
                background: "linear-gradient(93.56deg, #6535E9 4.6%, #4E33E9 96.96%)",
                borderRadius: 2,
                ml: 1,
                zIndex: 1,
              }}
              onClick={(e) => {
                e.preventDefault();
                handleSendMessage();
              }}
              disabled={isCheckingMessage}
            >
              <SendRounded sx={{ width: 18, height: 18, color: "white" }} />
            </Fab>
          </FlexRow>

          {/* Composer helper/error text */}
          {moderationError && (
            <Typography role="alert" sx={{ color: "error.main", px: 2, mt: -0.5, mb: 0.5, fontSize: 12 }}>
              {moderationError}
            </Typography>
          )}

          <FlexRow sx={{ px: 1, alignItems: "center", gap: 1 }}>
            <Button
              disableElevation
              variant="contained"
              startIcon={<MonetizationOn />}
              sx={{ borderRadius: 1.5 }}
              onClick={() => {
                onDonate(true);
              }}
            >
              <Typography sx={{ fontWeight: "600" }} variant="body2">
                Donate
              </Typography>
            </Button>

            <Typography sx={{ fontSize: 12 }}>to support your favourite streamers!</Typography>
          </FlexRow>
        </Box>
      </Box>

      <SenderPopover
        sender={msgSender}
        anchorElPopover={anchorElPopover}
        setAnchorElPopover={(anchorElPopover) => {
          updateChatState({
            anchorElPopover,
          });
        }}
      />

      <GiphyModal
        open={showGiphyModal}
        inputPlaceholder="Type something..."
        initialEditorState={editorState}
        onClose={() => {
          updateChatState({ showGiphyModal: false });
        }}
        onSelectItem={(data) => {
          sendGiphy({ ...data });
        }}
      />

      <GiftBoltsModal
        open={showGiftBox}
        onClose={() => onDonate(false)}
        receiverId={channelId}
        receiverName={receiverName}
        receiverType={receiverType}
      />
    </>
  );
};
